import type { Dispatch, ChangeEvent, SetStateAction } from 'react';
import type { PolicyViewModel } from 'src/api/types/PolicyViewModel';
import type { StateLookupOption, CodeDescriptionLookupOption } from 'src/api/services/policy.service';

import { memo } from 'react';

import InputAdornment from '@mui/material/InputAdornment';
import { Divider, MenuItem, TextField } from '@mui/material';

import { PolicyInformationInfoTable } from './policy-information-info-table';
import { PolicyInformationPolicyFields } from './policy-information-policy-fields';
import { tableFieldSx, type MarketOption } from './policy-information-edit.helpers';
import { PolicyInformationCoverageFields } from './policy-information-coverage-fields';
import { PolicyInformationLiabilityLimits } from './policy-information-liability-limits';

import type {
  PolicyEditDraft,
  PolicyFieldErrors,
} from './policy-information.types';

type PolicyInformationEditProps = {
  comboId: number;
  viewPolicy: PolicyViewModel;
  draft: PolicyEditDraft;
  setDraft: Dispatch<SetStateAction<PolicyEditDraft>>;
  stateLookups: StateLookupOption[];
  legalNatureLookups: CodeDescriptionLookupOption[];
  retroRatingLookups: CodeDescriptionLookupOption[];
  policyTypeLookups: CodeDescriptionLookupOption[];
  marketOptions: MarketOption[];
  fieldErrors: PolicyFieldErrors;
};

type EmployerStateRow = NonNullable<PolicyEditDraft['employerStatePremiums']>[number];

type EmployerStateSelectFieldProps = {
  row: EmployerStateRow;
  rowIndex: number;
  stateLookups: StateLookupOption[];
  setDraft: Dispatch<SetStateAction<PolicyEditDraft>>;
  error?: string;
  testId: string;
};

type EmployerStatePremiumFieldProps = {
  row: EmployerStateRow;
  rowIndex: number;
  setDraft: Dispatch<SetStateAction<PolicyEditDraft>>;
  error?: string;
  testId: string;
};

// Memoized so editing one row (of potentially 40+ multi-state rows) doesn't re-render every row's Select on each keystroke.
const EmployerStateSelectField = memo(function EmployerStateSelectFieldInner({
  row,
  rowIndex,
  stateLookups,
  setDraft,
  error,
  testId,
}: Readonly<EmployerStateSelectFieldProps>) {
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const stateCode = Number(event.target.value);
    const stateDisplay =
      stateLookups.find((lookup) => lookup.stateCode === stateCode)?.stateAbbreviation ?? event.target.value;

    setDraft((current) => ({
      ...current,
      employerStatePremiums: (current.employerStatePremiums ?? []).map((currentRow, index) =>
        index === rowIndex ? { ...currentRow, stateCode, stateDisplay } : currentRow
      ),
    }));
  };

  return (
    <TextField
      select
      fullWidth
      size="small"
      sx={tableFieldSx}
      value={row.stateCode > 0 ? String(row.stateCode) : ''}
      onChange={handleChange}
      error={Boolean(error)}
      helperText={error}
      data-testid={testId}
    >
      {stateLookups.map((lookup) => (
        <MenuItem key={lookup.stateCode} value={String(lookup.stateCode)}>
          {lookup.stateAbbreviation} - {lookup.stateName}
        </MenuItem>
      ))}
    </TextField>
  );
});

const EmployerStatePremiumField = memo(function EmployerStatePremiumFieldInner({ row, rowIndex, setDraft, error, testId }: Readonly<EmployerStatePremiumFieldProps>) {
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const premium = event.target.value.replace(/\D/g, '');
    setDraft((current) => ({
      ...current,
      employerStatePremiums: (current.employerStatePremiums ?? []).map((currentRow, index) =>
        index === rowIndex ? { ...currentRow, premium } : currentRow
      ),
    }));
  };

  return (
    <TextField
      fullWidth
      size="small"
      sx={tableFieldSx}
      value={row.premium}
      slotProps={{
        input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
        htmlInput: { inputMode: 'numeric', maxLength: 10 },
      }}
      onChange={handleChange}
      error={Boolean(error)}
      helperText={error}
      data-testid={testId}
    />
  );
});

export function PolicyInformationEdit({
  comboId,
  viewPolicy,
  draft,
  setDraft,
  stateLookups,
  legalNatureLookups,
  retroRatingLookups,
  policyTypeLookups,
  marketOptions,
  fieldErrors,
}: Readonly<PolicyInformationEditProps>) {
  const primaryCount = viewPolicy.employerStatesOfOperation?.primaryStates?.length ?? 0;
  const primaryRows = draft.employerStatePremiums?.slice(0, primaryCount) ?? [];
  const otherRows = draft.employerStatePremiums?.slice(primaryCount) ?? [];

  return (
    <>
      <PolicyInformationCoverageFields
        comboId={comboId}
        viewPolicy={viewPolicy}
        draft={draft}
        setDraft={setDraft}
        stateLookups={stateLookups}
        legalNatureLookups={legalNatureLookups}
        retroRatingLookups={retroRatingLookups}
        marketOptions={marketOptions}
        fieldErrors={fieldErrors}
      />
      <Divider sx={{ borderStyle: 'dashed', marginTop: '.25rem' }} />
      <PolicyInformationPolicyFields
        viewPolicy={viewPolicy}
        draft={draft}
        setDraft={setDraft}
        policyTypeLookups={policyTypeLookups}
        fieldErrors={fieldErrors}
      />

      {primaryRows.length > 0 && (
        <PolicyInformationInfoTable
          dataTestId="policy-state-table"
          title="Employer's primary state(s) of operation"
          columns={['State', 'Effective date', 'Expiration date', 'Premium']}
          rows={primaryRows.map((row, index) => {
            const rowErrors = fieldErrors.employerStatePremiums?.[index] ?? {};
            return {
              key: `primary-${row.rowKey}`,
              cells: [
                // Primary classification is derived from premium ranking; the state control is locked.
                <span key={`state-${row.rowKey}`} data-testid={`employer-state-locked-${row.statePremiumId}`}>
                  {row.stateDisplay}
                </span>,
                row.effectiveDate,
                row.expirationDate,
                <EmployerStatePremiumField
                  key={`premium-${row.rowKey}`}
                  row={row}
                  rowIndex={index}
                  setDraft={setDraft}
                  error={rowErrors.premium}
                  testId={`employer-state-premium-${row.statePremiumId}`}
                />,
              ],
            };
          })}
        />
      )}
      {otherRows.length > 0 && (
        <PolicyInformationInfoTable
          dataTestId="other-state-table"
          title="Employer's other state(s) of operation"
          columns={['State', 'Effective date', 'Expiration date', 'Premium']}
          rows={otherRows.map((row, localIndex) => {
            const absoluteIndex = localIndex + primaryCount;
            const rowErrors = fieldErrors.employerStatePremiums?.[absoluteIndex] ?? {};
            return {
              key: `other-${row.rowKey}`,
              cells: [
                <EmployerStateSelectField
                  key={`state-${row.rowKey}`}
                  row={row}
                  rowIndex={absoluteIndex}
                  stateLookups={stateLookups}
                  setDraft={setDraft}
                  error={rowErrors.stateCode}
                  testId={`employer-state-select-${row.statePremiumId}`}
                />,
                row.effectiveDate,
                row.expirationDate,
                <EmployerStatePremiumField
                  key={`premium-${row.rowKey}`}
                  row={row}
                  rowIndex={absoluteIndex}
                  setDraft={setDraft}
                  error={rowErrors.premium}
                  testId={`employer-state-premium-${row.statePremiumId}`}
                />,
              ],
            };
          })}
        />
      )}

      <PolicyInformationLiabilityLimits
        draft={draft}
        setDraft={setDraft}
        fieldErrors={fieldErrors}
      />
    </>
  );
}
